import { useState, useEffect } from "react";
import logoImg from "../assets/logo.png";

const motivationalMessages = [
  { text: "Preparando seu ambiente...", emoji: "⚙️" },
  { text: "Inovação é o nosso combustível", emoji: "🚀" },
  { text: "Construindo o futuro da higienização", emoji: "🌟" },
  { text: "Tecnologia a serviço da excelência", emoji: "💡" },
  { text: "Transformando dados em resultados", emoji: "📊" },
  { text: "Conectando você ao sucesso", emoji: "🔗" },
  { text: "Cada detalhe faz a diferença", emoji: "✨" },
  { text: "Qualidade que você pode confiar", emoji: "🏆" },
];

interface LoginSplashScreenProps {
  onComplete: () => void;
  duration?: number;
}

const completionMessage = {
  text: "Bem-vindo! Vamos transformar juntos!",
  emoji: "🎉",
};

export function LoginSplashScreen({
  onComplete,
  duration = 4000,
}: LoginSplashScreenProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Alternar mensagens (apenas quando não estiver completo)
  useEffect(() => {
    if (isComplete) return;

    const messageInterval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentMessageIndex(prev => (prev + 1) % motivationalMessages.length);
        setFadeIn(true);
      }, 300);
    }, 1200);

    return () => clearInterval(messageInterval);
  }, [isComplete]);

  // Barra de progresso
  useEffect(() => {
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / duration) * 100, 100);
      setProgress(newProgress);

      if (elapsed >= duration) {
        clearInterval(progressInterval);
        setIsComplete(true);
        // Pequeno delay para mostrar a mensagem de conclusão antes de redirecionar
        setTimeout(() => {
          onComplete();
        }, 800);
      }
    }, 50);

    return () => clearInterval(progressInterval);
  }, [duration, onComplete]);

  const currentMessage = isComplete ? completionMessage : motivationalMessages[currentMessageIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-[#0085BD] via-[#006a96] to-[#004d6d] overflow-hidden">
      {/* Background decorativo animado */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Círculos flutuantes */}
        <div className="absolute top-1/4 -left-20 w-40 h-40 bg-white/5 rounded-full blur-2xl animate-pulse" />
        <div className="absolute bottom-1/4 -right-20 w-60 h-60 bg-[#92C163]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "0.5s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

        {/* Partículas flutuantes */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      {/* Conteúdo principal */}
      <div className="relative z-10 flex flex-col items-center justify-center px-4">
        {/* Logo container com animação */}
        <div className="relative mb-8">
          {/* Anel pulsante externo */}
          <div className="absolute inset-0 -m-6 rounded-3xl border-2 border-white/10 animate-ping" style={{ animationDuration: "2s" }} />
          <div className="absolute inset-0 -m-4 rounded-2xl border border-white/20 animate-pulse" />

          {/* Logo */}
          <div className="relative bg-white rounded-2xl p-6 shadow-2xl transform animate-float-slow">
            <img
              className="w-48 h-auto"
              src={logoImg}
              alt="Logo Luvilimp"
            />

            {/* Brilho no logo */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent rounded-2xl animate-shimmer" />
          </div>
        </div>

        {/* Mensagem animada */}
        <div className="h-20 flex flex-col items-center justify-center">
          <div
            className={`flex items-center gap-3 transition-all duration-300 ${
              fadeIn || isComplete ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
            } ${isComplete ? "scale-110" : ""}`}
          >
            <span className={`text-3xl ${isComplete ? "animate-bounce" : ""}`}>{currentMessage.emoji}</span>
            <p className={`text-lg md:text-xl font-medium text-center ${
              isComplete ? "text-white font-bold" : "text-white/90"
            }`}>
              {currentMessage.text}
            </p>
            {isComplete && <span className="text-3xl animate-bounce" style={{ animationDelay: "0.1s" }}>🚀</span>}
          </div>
        </div>

        {/* Barra de progresso */}
        <div className="w-64 md:w-80 mt-8">
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className={`h-full rounded-full transition-all duration-100 ease-linear relative ${
                isComplete
                  ? "bg-gradient-to-r from-[#92C163] via-[#b8e07a] to-[#92C163]"
                  : "bg-gradient-to-r from-[#92C163] via-[#a8d176] to-[#92C163]"
              }`}
              style={{ width: `${progress}%` }}
            >
              {/* Efeito de brilho na barra */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
            </div>
          </div>
          <p className={`text-xs text-center mt-3 transition-all duration-300 ${
            isComplete ? "text-[#92C163] font-semibold" : "text-white/50"
          }`}>
            {isComplete ? "✓ Pronto!" : `${Math.round(progress)}% carregado`}
          </p>
        </div>

        {/* Indicador de loading - oculto quando completo */}
        {!isComplete && (
          <div className="mt-8 flex items-center gap-2">
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <div
                  key={i}
                  className="w-2 h-2 bg-white/60 rounded-full animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Ícone de sucesso quando completo */}
        {isComplete && (
          <div className="mt-8 flex items-center justify-center">
            <div className="w-10 h-10 bg-[#92C163] rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-[#92C163]/30">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Estilos de animação customizados */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.6;
          }
        }

        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-float {
          animation: float 4s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 3s ease-in-out infinite;
        }

        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
